<template>
  <div
    v-if="loadingDetail"
    class="eu-pa_6 eu-align_center"
  >
    <emd-loader :type="'_invert'" />
  </div>
  <div
    v-else
    class="el-stripe"
  >
    <div class="el-section">
      <div class="el-grid-list">
        <div class="emd-container">
          <div
            class="emd-status-bar"
            v-bind:class="statusClass(invoice.status)"
          ></div>
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('invoice.detail.status') }}
              </h4>
            </div>
            <span
              class="emd-badge"
              v-bind:class="statusClass(invoice.status)"
              >{{ invoice.status }}</span
            >
          </div>
        </div>
        <div class="emd-container">
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('invoice.detail.amount') }}
              </h4>
            </div>
            <p class="emd-text _size_section">{{ invoice.amount }}</p>
          </div>
        </div>
        <div class="emd-container">
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('invoice.detail.payment-method') }}
              </h4>
            </div>
            <p class="emd-text _size_section">{{ invoice.payment_method }}</p>
          </div>
        </div>
        <div class="emd-container">
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('invoice.detail.due-at') }}
              </h4>
            </div>
            <p class="emd-text _size_section">
              {{ invoice.due_at | dateFormat('date') }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="el-section">
      <div class="emd-container _highlight_header">
        <div class="emd-container__header">
          <h2 class="emd-text _size_section">
            {{ $t('invoice.detail.info') }}
          </h2>
        </div>
        <div class="emd-container__content">
          <div class="emd-panel">
            <div class="emd-panel__section">
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <h2 class="emd-text _color_default _type_caption">
                    {{ $t('invoice.detail.general-data') }}
                  </h2>
                </div>
              </div>
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('invoice.detail.id') }}
                  </p>
                  <span>{{ invoice.id }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('invoice.detail.code') }}
                  </p>
                  <span>{{ invoice.code }}</span>
                </div>
                <div
                  class="el-chunk"
                  v-if="Object.entries(invoice.subscription).length"
                >
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('invoice.detail.origin') }}
                  </p>
                  <router-link
                    v-if="permissionIsEnabled('subscription:get')"
                    :to="{
                      name: 'subscriptionsDetail',
                      params: { id: invoice.subscription.id, tab: 'info' }
                    }"
                    class="emd-link"
                    >{{ invoice.subscription.id }}</router-link
                  >
                  <p
                    v-else
                    class="emd-text"
                  >
                    {{ invoice.subscription.id || '-' }}
                  </p>
                </div>
                <div
                  class="el-chunk"
                  v-if="invoice.installments"
                >
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('invoice.detail.installments') }}
                  </p>
                  <span>{{ invoice.installments }}</span>
                </div>
                <div
                  class="el-chunk"
                  v-if="
                    invoice.metadata && Object.entries(invoice.metadata).length
                  "
                >
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('invoice.detail.more-info') }}
                  </p>
                  <pre>{{ invoice.metadata }}</pre>
                </div>
              </div>
            </div>
          </div>
          <div class="emd-panel">
            <div class="emd-panel__section">
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <h2 class="emd-text _color_default _type_caption">
                    {{ $t('invoice.detail.customer.title') }}
                  </h2>
                </div>
              </div>
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('invoice.detail.customer.name') }}
                  </p>
                  <router-link
                    v-if="permissionIsEnabled('customer:get')"
                    :to="{
                      name: 'customersDetail',
                      params: { id: invoice.customer.id, tab: 'info' }
                    }"
                    class="emd-link"
                    >{{ invoice.customer.name || '-' }}</router-link
                  >
                  <p
                    v-else
                    class="emd-text"
                  >
                    {{ invoice.customer.name || '-' }}
                  </p>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('invoice.detail.customer.email') }}
                  </p>
                  <span>{{ invoice.customer.email || '-' }}</span>
                </div>
                <div
                  class="el-chunk"
                  v-if="
                    invoice.customer.phones &&
                    invoice.customer.phones.home_phone
                  "
                >
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('invoice.detail.customer.phone') }}
                  </p>
                  <span
                    >{{ invoice.customer.phones.home_phone.country_code }}
                    {{ invoice.customer.phones.home_phone.area_code }}
                    {{ invoice.customer.phones.home_phone.number }}</span
                  >
                </div>
                <div
                  class="el-chunk"
                  v-if="
                    invoice.customer.phones &&
                    invoice.customer.phones.mobile_phone
                  "
                >
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('invoice.detail.customer.mobile-phone') }}
                  </p>
                  <span
                    >{{ invoice.customer.phones.mobile_phone.country_code }}
                    {{ invoice.customer.phones.mobile_phone.area_code }}
                    {{ invoice.customer.phones.mobile_phone.number }}</span
                  >
                </div>
                <div
                  class="el-chunk"
                  v-if="invoice.customer.address"
                >
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('invoice.detail.customer.address') }}
                  </p>
                  <span>
                    {{ invoice.customer.address.street }}
                    {{ invoice.customer.address.number }} -
                    {{ invoice.customer.address.complement }} -
                    {{ invoice.customer.address.neighborhood }},
                    {{ invoice.customer.address.city }},
                    {{ invoice.customer.address.state }} -
                    {{ invoice.customer.address.country }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Itens -->
    <div class="el-section">
      <div class="emd-container _highlight_header">
        <div class="emd-container__header">
          <h2 class="emd-text _size_section">
            {{ $t('invoice.detail.items.title') }}
          </h2>
        </div>
        <div class="emd-table-responsive">
          <Table
            :fields="fields"
            :items="invoice.items"
          ></Table>
        </div>
      </div>
    </div>
    <!-- Cobrança -->
    <div
      class="el-section"
      v-if="invoice.charge"
    >
      <div class="emd-container _highlight_header">
        <div class="emd-container__header">
          <h2 class="emd-text _size_section">
            {{ $t('invoice.detail.charge.title') }}
          </h2>
        </div>
        <div class="emd-card-grid">
          <div class="emd-card-grid__content">
            <div class="eu-clearfix">
              <div class="eu-float_left">
                <h2
                  class="emd-text _type_caption _size_section eu-pb_2 eu-pr_2 eu_no_padding_sm"
                >
                  <router-link
                    v-if="permissionIsEnabled('charge:get')"
                    :to="{
                      name: 'chargesDetail',
                      params: { id: invoice.charge.id }
                    }"
                    class="emd-link"
                    >{{ invoice.charge.id }}</router-link
                  >
                  <p
                    v-else
                    class="emd-text"
                  >
                    {{ invoice.charge.id || '-' }}
                  </p>
                </h2>
              </div>
              <div class="eu-float_left_mobile eu-float_right">
                <span
                  class="emd-badge"
                  v-bind:class="statusClass(invoice.charge.status)"
                  >{{ invoice.charge.status }}</span
                >
              </div>
            </div>
          </div>
          <div class="emd-card-grid__row">
            <div class="emd-card-grid__column">
              <label class="emd-text _type_label _size_tiny _color_subtle">{{
                $t('invoice.detail.charge.amount')
              }}</label>
              <p class="emd-card-grid__line">{{ invoice.charge.amount }}</p>
            </div>
            <div
              class="emd-card-grid__column"
              v-if="invoice.charge.paid_at"
            >
              <label class="emd-text _type_label _size_tiny _color_subtle">{{
                $t('invoice.detail.charge.paid-at')
              }}</label>
              <p
                class="emd-card-grid__line"
                v-if="invoice.charge.payment_method === 'Boleto'"
              >
                {{ invoice.charge.paid_at | dateFormat('date') }}
              </p>
              <p
                class="emd-card-grid__line"
                v-else
              >
                {{ invoice.charge.paid_at | dateFormat('dateAndHour') }}
              </p>
            </div>
            <div
              class="emd-card-grid__column"
              v-if="invoice.charge.due_at"
            >
              <label class="emd-text _type_label _size_tiny _color_subtle">{{
                $t('invoice.detail.charge.due-at')
              }}</label>
              <p class="emd-card-grid__line">
                {{ invoice.charge.due_at | dateFormat('date') }}
              </p>
            </div>
            <div class="emd-card-grid__column">
              <label class="emd-text _type_label _size_tiny _color_subtle">{{
                $t('invoice.detail.charge.payment-method')
              }}</label>
              <p class="emd-card-grid__line">
                {{ invoice.charge.payment_method }}
              </p>
            </div>
            <div class="emd-card-grid__column">
              <label class="emd-text _type_label _size_tiny _color_subtle">{{
                $t('invoice.detail.charge.created-at')
              }}</label>
              <p class="emd-card-grid__line">
                {{ invoice.charge.created_at | dateFormat('dateAndHour') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <log-links
      :entity="invoice"
      :title="$t('log-links.invoice')"
    ></log-links>
  </div>
</template>
<script>
import { EmdLoader } from 'emerald-vue/src/components'
import Table from '@/components/project/molecules/Table.vue'
import { statusClass } from '@/filters'
import LogLinks from '@/components/project/molecules/LogLinks/index.vue'
export default {
  name: 'InvoiceDetail',
  components: {
    EmdLoader,
    Table,
    LogLinks
  },
  data () {
    return {
      statusClass: statusClass,
      invoice: null,
      loadingDetail: true,
      hasName: Boolean,
      hasDescription: Boolean
    }
  },
  computed: {
    params () {
      return {
        merchantId: this.$store.getters.merchantId,
        accountId: this.$store.getters.accountId,
        invoiceId: this.$route.params.id
      }
    },
    fields () {
      return [
        {
          id: this.hasName ? 'name' : this.hasDescription ? 'description' : '',
          label: 'invoice.detail.items.name',
          type: 'text'
        },
        {
          id: 'quantity',
          label: 'invoice.detail.items.quantity',
          type: 'text'
        },
        {
          id: 'pricing_scheme.scheme_type',
          label: 'invoice.detail.items.scheme-type',
          type: 'text'
        },
        {
          id: 'amount',
          label: 'invoice.detail.items.amount',
          type: 'text'
        }
      ]
    }
  },
  created () {
    this.$store.dispatch('GET_INVOICE', this.params).then(res => {
      this.hasName = res.items[0].name ? res.items[0].name.length >= 2 : false
      this.hasDescription = res.items[0].description
        ? res.items[0].description.length >= 2
        : false
      this.invoice = res
      this.loadingDetail = false
    })
  },
  mounted () {
    if (!this.$route.params.id.includes('in_')) {
      this.$router.push({ name: 'invoices' }).catch(err => err)
    }
  },
  methods: {
    permissionIsEnabled (action) {
      return this.$store.getters.permissionIsEnabled(action)
    }
  }
}
</script>
